/**
 * Pagina /tesoreria/compensaciones — Sprint 17 Fase 2 refactor a cards.
 *
 * Historia: Sprint 12 S12-15 (listar + aplicar) + S12-18 (crear),
 * migrada al portal en S13-05 tras la doctrina Portal vs Superadmin.
 * Sprint 17 Fase 2 refactoriza la vista de tabla a cards visuales con
 * stats (monto / consumido / restante), barra de progreso y grid de
 * aplicaciones mes a mes. Tambien introduce periodicidad 'unica' para
 * compensaciones de una sola aplicacion.
 *
 * Arquitectura: View + wrapper.
 * - CompensacionesView: componente puro, recibe data y handlers.
 * - CompensacionesPage: container que hace fetch y maneja modales.
 *
 * RBAC: requireRole='tesorero' + tipo_cuenta='operativa'.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { listCategorias } from '../../lib/api/categorias';
import { CategoriaSelector } from '../../components/tesoreria/CategoriaSelector';
import { InputFecha } from '../../components/ui/InputFecha';
import { InputMontoCLP } from '../../components/ui/InputMontoCLP';
import {
  aplicarCompensacion,
  createCompensacion,
  listCompensacionesConDetalle,
  previewAplicacionCompensacion,
} from '../../lib/api/compensaciones';
import { listLotes } from '../../lib/api/lotes';
import { listTenencias } from '../../lib/api/tenencias';
import type {
  AplicarCompensacionResponse,
  CategoriaRead,
  CompensacionConDetalle,
  CompensacionCreate,
  LoteResumen,
  PreviewAplicacionResponse,
  TenenciaConPersonaLote,
} from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { CompensacionCard } from '../../components/tesoreria/CompensacionCard';
import { ModalEditarCompensacion } from '../../components/tesoreria/ModalEditarCompensacion';
import { ModalCerrarCompensacion } from '../../components/tesoreria/ModalCerrarCompensacion';
import { ModalConfirmarEliminar } from '../../components/tesoreria/ModalConfirmarEliminar';
import { formatFecha } from '../../lib/format';
import { hoy } from '../../lib/tiempo';
import { Monto, Num } from '../../components/tesoreria/Monto';

// ===== Modal de creación de compensación =====

interface ModalCrearProps {
  onCerrar: () => void;
  onCreada: () => void;
}

function ModalCrear({ onCerrar, onCreada }: ModalCrearProps) {
  const [lotes, setLotes] = useState<LoteResumen[]>([]);
  const [tenencias, setTenencias] = useState<TenenciaConPersonaLote[]>([]);
  const [categorias, setCategorias] = useState<CategoriaRead[]>([]);
  const [loadingMaestros, setLoadingMaestros] = useState(true);
  const [errorMaestros, setErrorMaestros] = useState<string | null>(null);

  const [loteId, setLoteId] = useState('');
  const [tenenciaId, setTenenciaId] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [descripcion, setDescripcion] = useState('');
  // Sprint 17 S17-Z13: state numérico con InputMontoCLP.
  const [monto, setMonto] = useState<number | null>(null);
  const [periodicidad, setPeriodicidad] =
    useState<CompensacionCreate['periodicidad']>('mensual');
  const [vigenteDesde, setVigenteDesde] = useState('');
  const [vigenteHasta, setVigenteHasta] = useState('');
  const [notas, setNotas] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    Promise.all([
      listLotes({ activo: true }),
      listCategorias({ activa: true }),
    ])
      .then(([ls, cs]) => {
        if (cancelado) return;
        setLotes(ls);
        setCategorias(cs);
      })
      .catch((err) => {
        if (cancelado) return;
        setErrorMaestros(
          err instanceof ApiError
            ? `Error ${err.status} al cargar lotes/categorías`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoadingMaestros(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!loteId) {
      setTenencias([]);
      setTenenciaId('');
      return;
    }
    let cancelado = false;
    listTenencias({ lote_id: loteId, activas: true })
      .then((ts) => {
        if (cancelado) return;
        setTenencias(ts);
        if (ts.length === 1) setTenenciaId(ts[0]!.id);
      })
      .catch(() => {
        if (cancelado) return;
        setTenencias([]);
      });
    return () => {
      cancelado = true;
    };
  }, [loteId]);

  const montoNum = monto ?? 0;
  const formValido =
    loteId !== '' &&
    tenenciaId !== '' &&
    categoriaId !== '' &&
    descripcion.trim().length > 0 &&
    montoNum > 0 &&
    vigenteDesde !== '';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!formValido || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: CompensacionCreate = {
        lote_id: loteId,
        tenencia_id: tenenciaId,
        descripcion: descripcion.trim(),
        categoria_id: categoriaId,
        monto: montoNum,
        periodicidad,
        vigente_desde: vigenteDesde,
        vigente_hasta: vigenteHasta || null,
        notas: notas.trim() || null,
        activa: true,
      };
      await createCompensacion(payload);
      onCreada();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body !== null && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setSubmitError(detail);
      } else {
        setSubmitError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Nueva compensación"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Nueva compensación
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="bg-orange/[0.08] border-l-4 border-orange p-3 rounded-sm">
          <p className="text-xs text-text-secondary">
            Una compensación crea <strong>Gasto + acreditación de cuotas</strong>{' '}
            de la casa al aplicarse. La cuenta bancaria del barrio no se mueve
            (efecto $0 en banco). Periodicidad <strong>única</strong> = se
            aplica una sola vez y se cierra.
          </p>
        </div>

        {loadingMaestros && (
          <p className="text-sm text-text-muted">Cargando casas y categorías…</p>
        )}
        {errorMaestros && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {errorMaestros}
          </div>
        )}

        {!loadingMaestros && !errorMaestros && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm space-y-1">
                <span className="text-text-secondary">Casa</span>
                <select
                  value={loteId}
                  onChange={(e) => setLoteId(e.target.value)}
                  className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
                  required
                >
                  <option value="">— elige —</option>
                  {lotes.map((l) => (
                    <option key={l.id} value={l.id}>
                      Casa {l.identificador}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm space-y-1">
                <span className="text-text-secondary">Tenencia (responsable)</span>
                <select
                  value={tenenciaId}
                  onChange={(e) => setTenenciaId(e.target.value)}
                  className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary disabled:opacity-50"
                  required
                  disabled={!loteId}
                >
                  <option value="">
                    {loteId ? '— elige —' : 'elige una casa primero'}
                  </option>
                  {tenencias.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.persona_nombre_completo} ({t.tipo})
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="text-sm space-y-1 block">
              <span className="text-text-secondary">Descripción</span>
              <input
                type="text"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Ej: Celulares citofonía 1er semestre 2026"
                className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
                required
              />
            </label>

            <div className="grid grid-cols-3 gap-3">
              <label className="text-sm space-y-1">
                <span className="text-text-secondary">Categoría</span>
                <CategoriaSelector
                  categorias={categorias.filter((c) => c.tipo === 'gasto' && c.activa)}
                  value={categoriaId}
                  onChange={setCategoriaId}
                  required
                  className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
                />
              </label>

              <label className="text-sm space-y-1">
                <span className="text-text-secondary">
                  {periodicidad === 'unica' ? 'Monto total (CLP)' : 'Monto por periodo'}
                </span>
                <InputMontoCLP
                  value={monto}
                  onChange={setMonto}
                  placeholder="30000"
                  className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
                />
              </label>

              <label className="text-sm space-y-1">
                <span className="text-text-secondary">Periodicidad</span>
                <select
                  value={periodicidad}
                  onChange={(e) =>
                    setPeriodicidad(
                      e.target.value as CompensacionCreate['periodicidad'],
                    )
                  }
                  className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
                  required
                >
                  <option value="unica">única (una sola vez)</option>
                  <option value="mensual">mensual</option>
                  <option value="bimestral">bimestral</option>
                  <option value="semestral">semestral</option>
                  <option value="anual">anual</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm space-y-1">
                <span className="text-text-secondary">Vigente desde</span>
                <InputFecha
                  value={vigenteDesde}
                  onChange={setVigenteDesde}
                  className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
                  requerido
                />
              </label>

              <label className="text-sm space-y-1">
                <span className="text-text-secondary">
                  {periodicidad === 'unica'
                    ? 'Vigente hasta (opcional, para fijar plazo)'
                    : 'Vigente hasta (opcional)'}
                </span>
                <InputFecha
                  value={vigenteHasta}
                  onChange={setVigenteHasta}
                  className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
                />
              </label>
            </div>

            <label className="text-sm space-y-1 block">
              <span className="text-text-secondary">Notas (opcional)</span>
              <textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                rows={2}
                className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              />
            </label>
          </>
        )}

        {submitError && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {submitError}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!formValido || submitting}
          >
            {submitting ? 'Creando…' : 'Crear compensación'}
          </Button>
        </div>
      </form>
    </div>
  );
}

interface ModalAplicarProps {
  compensacion: CompensacionConDetalle;
  onCerrar: () => void;
  onAplicada: (resp: AplicarCompensacionResponse) => void;
}

/**
 * Sprint 17 Z8 — ModalAplicar reescrito.
 *
 * Doctrina nueva: una sola aplicación total por compensación, greedy
 * desde cuota más antigua, sobrante a saldo_a_favor del lote. El
 * backend calcula el desglose; el tesorero solo confirma.
 *
 * Flujo:
 * 1. Al montar, fetch al endpoint /preview-aplicacion (sin escribir).
 * 2. Muestra tabla read-only "cuota → monto" + sobrante + descripción
 *    propuesta del Gasto.
 * 3. Tesorero confirma con checkbox → POST /aplicar.
 */
function ModalAplicar({
  compensacion,
  onCerrar,
  onAplicada,
}: ModalAplicarProps) {
  const fechaHoy = hoy();
  const [fechaAplicacion, setFechaAplicacion] = useState(fechaHoy);
  const [proveedor, setProveedor] = useState('');
  const [preview, setPreview] = useState<PreviewAplicacionResponse | null>(null);
  const [cargandoPreview, setCargandoPreview] = useState(true);
  const [errorPreview, setErrorPreview] = useState<string | null>(null);
  const [confirmado, setConfirmado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setCargandoPreview(true);
    setErrorPreview(null);
    previewAplicacionCompensacion(compensacion.id)
      .then((p) => {
        if (!cancelado) setPreview(p);
      })
      .catch((err) => {
        if (cancelado) return;
        if (err instanceof ApiError) {
          const detail =
            typeof err.body === 'object' && err.body && 'detail' in err.body
              ? String((err.body as { detail: unknown }).detail)
              : `Error ${err.status}`;
          setErrorPreview(detail);
        } else {
          setErrorPreview('Error al cargar preview de aplicación');
        }
      })
      .finally(() => {
        if (!cancelado) setCargandoPreview(false);
      });
    return () => {
      cancelado = true;
    };
  }, [compensacion.id]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!confirmado) {
      setError('Marca la confirmación antes de aplicar.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const resp = await aplicarCompensacion(compensacion.id, {
        fecha_aplicacion: fechaAplicacion,
        proveedor: proveedor.trim() || null,
      });
      onAplicada(resp);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body && 'detail' in err.body
            ? typeof (err.body as { detail: unknown }).detail === 'string'
              ? String((err.body as { detail: unknown }).detail)
              : JSON.stringify((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red al aplicar la compensación.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-aplicar-titulo"
    >
      <div
        className="bg-bg-elevated border-2 border-admin-red rounded-md p-6 max-w-2xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="modal-aplicar-titulo"
          className="text-xl font-medium text-text-primary mb-2"
        >
          Aplicar compensación
        </h2>
        <p className="text-sm text-text-secondary mb-4">
          {compensacion.descripcion} · <Monto value={compensacion.monto} /> ·{' '}
          {compensacion.periodicidad}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {cargandoPreview && (
            <p className="text-sm text-text-muted">Calculando aplicación…</p>
          )}

          {errorPreview && !cargandoPreview && (
            <div
              className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
              role="alert"
            >
              {errorPreview}
            </div>
          )}

          {preview && !cargandoPreview && (
            <>
              <div className="bg-bg-sunken border-[0.5px] border-white/15 rounded-sm overflow-hidden">
                <div className="px-3 py-2 text-xs uppercase tracking-wide text-text-secondary border-b border-white/[0.06] flex justify-between">
                  <span>Aplicación greedy</span>
                  <span>
                    {preview.lineas.length}{' '}
                    {preview.lineas.length === 1 ? 'cuota' : 'cuotas'}
                  </span>
                </div>
                {preview.lineas.length === 0 ? (
                  <p className="text-sm text-text-muted px-3 py-3">
                    La casa no tiene cuotas con saldo. El monto completo se
                    acreditará a saldo a favor del lote.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs text-text-muted border-b border-white/[0.04]">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-normal">Cuota</th>
                        <th className="text-right px-3 py-1.5 font-normal">
                          Saldo antes
                        </th>
                        <th className="text-right px-3 py-1.5 font-normal">
                          Se aplica
                        </th>
                        <th className="text-right px-3 py-1.5 font-normal">
                          Saldo después
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.lineas.map((l) => (
                        <tr
                          key={l.cuota_id}
                          className="border-b border-white/[0.04] last:border-0"
                        >
                          <td className="px-3 py-1.5 text-text-primary">
                            {l.cuota_periodo}
                            <span className="text-text-muted ml-2 text-xs">
                              vence {formatFecha(l.cuota_vence_el)}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-right text-text-secondary">
                            <Monto value={l.saldo_antes} />
                          </td>
                          <td className="px-3 py-1.5 text-right text-text-primary font-medium">
                            <Monto value={l.monto_aplicado} />
                          </td>
                          <td className="px-3 py-1.5 text-right text-text-secondary">
                            <Monto value={l.saldo_despues} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="px-3 py-2 text-xs text-text-secondary border-t border-white/[0.06] flex justify-between bg-bg-card/30">
                  <span>
                    Total compensación: <strong className="text-text-primary"><Monto value={preview.monto_total} /></strong>
                  </span>
                  <span>
                    {preview.sobrante_a_saldo_a_favor > 0
                      ? <>Sobrante → saldo a favor: <strong className="text-state-success"><Monto value={preview.sobrante_a_saldo_a_favor} /></strong></>
                      : 'Sin sobrante'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="fecha-aplicacion"
                    className="block text-sm font-medium text-text-primary mb-1"
                  >
                    Fecha de aplicación
                  </label>
                  <InputFecha
                    id="fecha-aplicacion"
                    requerido
                    value={fechaAplicacion}
                    onChange={setFechaAplicacion}
                    className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
                  />
                </div>
                <div>
                  <label
                    htmlFor="proveedor"
                    className="block text-sm font-medium text-text-primary mb-1"
                  >
                    Proveedor (opcional)
                  </label>
                  <input
                    id="proveedor"
                    type="text"
                    maxLength={255}
                    value={proveedor}
                    onChange={(e) => setProveedor(e.target.value)}
                    placeholder="Default: vecino"
                    className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 placeholder:text-text-muted"
                  />
                </div>
              </div>

              <div className="text-xs text-text-muted bg-bg-sunken px-3 py-2 rounded-sm">
                <strong>Descripción del Gasto:</strong>{' '}
                <code className="font-mono text-text-secondary">
                  {preview.descripcion_gasto_propuesta}
                </code>
              </div>

              <div className="bg-admin-red/[0.08] border-l-4 border-admin-red p-3 rounded-sm">
                <p className="text-sm text-text-primary mb-2">
                  Al confirmar, se crea <strong>1 Gasto</strong>,{' '}
                  <strong>1 Pago virtual</strong> y{' '}
                  <strong><Num>{preview.lineas.length + (preview.sobrante_a_saldo_a_favor > 0 ? 1 : 0)}</Num>{' '}
                  distribución(es)</strong>. La cuenta bancaria del barrio no
                  se mueve. <strong>Esta operación es única e irreversible</strong>:
                  la compensación quedará marcada como aplicada.
                </p>
                <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmado}
                    onChange={(e) => setConfirmado(e.target.checked)}
                    className="rounded-sm accent-admin-red"
                  />
                  <span>Confirmo la aplicación contable de esta compensación.</span>
                </label>
              </div>
            </>
          )}

          {error && (
            <div
              className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="ghost" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                enviando ||
                cargandoPreview ||
                errorPreview !== null ||
                !preview ||
                !confirmado
              }
            >
              {enviando ? 'Aplicando…' : 'Aplicar compensación'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===== View (componente puro) =====

export interface CompensacionesViewProps {
  compensaciones: CompensacionConDetalle[];
  loading: boolean;
  error: string | null;
  onAplicar: (c: CompensacionConDetalle) => void;
  onEditar: (c: CompensacionConDetalle) => void;
  onCerrar: (c: CompensacionConDetalle) => void;
  onEliminar: (c: CompensacionConDetalle) => void;
  onCrearNueva: () => void;
  mensaje: string | null;
  onCerrarMensaje: () => void;
}

export function CompensacionesView({
  compensaciones,
  loading,
  error,
  onAplicar,
  onEditar,
  onCerrar,
  onEliminar,
  onCrearNueva,
  mensaje,
  onCerrarMensaje,
}: CompensacionesViewProps) {
  const activas = compensaciones.filter((c) => c.activa);
  const cerradas = compensaciones.filter((c) => !c.activa);

  return (
    <div className="px-4 md:px-8 py-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-orange font-medium">
            Tesorería
          </div>
          <h1 className="text-2xl font-medium text-text-primary mt-1">
            Compensaciones (pago en especie)
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Acuerdos donde un vecino asume un servicio del barrio y se le
            acreditan cuotas. Al aplicar, se crea Gasto + acreditación a
            cuotas en una sola operación — efecto $0 en banco.
          </p>
        </div>
        <Button variant="primary" onClick={onCrearNueva}>
          + Nueva compensación
        </Button>
      </div>

      {mensaje && (
        <div
          className="bg-state-success/15 border-l-4 border-state-success p-3 rounded-sm text-sm text-text-primary"
          role="status"
        >
          {mensaje}{' '}
          <button
            type="button"
            onClick={onCerrarMensaje}
            className="underline ml-2"
          >
            Cerrar
          </button>
        </div>
      )}

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}

      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}

      {!loading && !error && compensaciones.length === 0 && (
        <p className="text-sm text-text-muted">
          No hay compensaciones registradas. Usa "+ Nueva compensación" para
          registrar el primer acuerdo de pago en especie.
        </p>
      )}

      {!loading && !error && activas.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-text-secondary uppercase tracking-wide">
            Activas ({activas.length})
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {activas.map((c) => (
              <CompensacionCard
                key={c.id}
                compensacion={c}
                onAplicar={onAplicar}
                onEditar={onEditar}
                onCerrar={onCerrar}
                onEliminar={onEliminar}
              />
            ))}
          </div>
        </section>
      )}

      {!loading && !error && cerradas.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-text-secondary uppercase tracking-wide">
            Cerradas ({cerradas.length})
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 opacity-70">
            {cerradas.map((c) => (
              <CompensacionCard
                key={c.id}
                compensacion={c}
                onAplicar={onAplicar}
                onEditar={onEditar}
                onCerrar={onCerrar}
                onEliminar={onEliminar}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ===== Wrapper (container con fetch + modales) =====

export function CompensacionesPage() {
  const [compensaciones, setCompensaciones] = useState<
    CompensacionConDetalle[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seleccionada, setSeleccionada] = useState<
    CompensacionConDetalle | null
  >(null);
  const [editando, setEditando] = useState<CompensacionConDetalle | null>(null);
  const [cerrando, setCerrando] = useState<CompensacionConDetalle | null>(null);
  const [eliminando, setEliminando] = useState<
    CompensacionConDetalle | null
  >(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listCompensacionesConDetalle()
      .then((data) => {
        if (!cancelado) setCompensaciones(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar compensaciones`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey]);

  return (
    <>
      <CompensacionesView
        compensaciones={compensaciones}
        loading={loading}
        error={error}
        onAplicar={setSeleccionada}
        onEditar={setEditando}
        onCerrar={setCerrando}
        onEliminar={setEliminando}
        onCrearNueva={() => setMostrarCrear(true)}
        mensaje={mensaje}
        onCerrarMensaje={() => setMensaje(null)}
      />

      {seleccionada && (
        <ModalAplicar
          compensacion={seleccionada}
          onCerrar={() => setSeleccionada(null)}
          onAplicada={(resp) => {
            setSeleccionada(null);
            setMensaje(
              `Compensación aplicada: Gasto ${resp.gasto.id.slice(0, 8)} + Pago virtual ${resp.pago_virtual.num_operacion} + ${resp.distribuciones_creadas} ${resp.distribuciones_creadas === 1 ? 'línea' : 'líneas'} de distribución.`,
            );
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {mostrarCrear && (
        <ModalCrear
          onCerrar={() => setMostrarCrear(false)}
          onCreada={() => {
            setMostrarCrear(false);
            setMensaje('Compensación creada.');
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {editando && (
        <ModalEditarCompensacion
          compensacion={editando}
          onCerrar={() => setEditando(null)}
          onGuardada={() => {
            setEditando(null);
            setMensaje('Compensación actualizada.');
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {cerrando && (
        <ModalCerrarCompensacion
          compensacion={cerrando}
          onCerrar={() => setCerrando(null)}
          onCerrada={() => {
            setCerrando(null);
            setMensaje('Compensación cerrada.');
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {eliminando && (
        <ModalConfirmarEliminar
          compensacion={eliminando}
          onCerrar={() => setEliminando(null)}
          onEliminada={() => {
            setEliminando(null);
            setMensaje('Compensación eliminada.');
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
}
